/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { defineMessages } from 'react-intl';

export const ServiceDesk = defineMessages({
  connectAccountButton: {
    id: 'ServiceDesk.connectAccountButton',
    defaultMessage: 'Connect account',
    description: ''
  },
  connected: {
    id: 'ServiceDesk.connected',
    defaultMessage: 'Connected',
    description: ''
  },
  connecting: {
    id: 'ServiceDesk..connecting',
    defaultMessage: 'Attempting to connect...',
    description: ''
  },
  generalHelpDeskLabel: {
    id: 'ServiceDesk.generalHelpDeskLabel',
    defaultMessage: 'General help desk',
    description: ''
  },
  githubLogoLabel: {
    id: 'ServiceDesk.githubLogoLabel',
    defaultMessage: 'GitHub logo',
    description: ''
  },
  githubTitle: {
    id: 'ServiceDesk.githubTitle',
    defaultMessage: 'Get started on GitHub',
    description: ''
  },
  webchatTitle: {
    id: 'ServiceDesk.webchatTitle',
    defaultMessage: 'Extending web chat',
    description: ''
  },
  logoLabel: {
    id: 'ServiceDesk.logoLabel',
    defaultMessage: ' logo',
    description: ''
  },
  // TODO -- this shouldn't be a translated string but a constant we supply elsewhere
  maximumFieldLength: {
    id: 'ServiceDesk.maximumFieldLength',
    defaultMessage: '150'
  },
  notConnected: {
    id: 'ServiceDesk.notConnected',
    defaultMessage: 'Not connected',
    description: ''
  },
  notConnectedDescription: {
    id: 'ServiceDesk.notConnectedDescription',
    defaultMessage: 'Cannot connect to Zendesk',
    description: ''
  },
  rightArrowLabel: {
    id: 'ServiceDesk.rightArrowLabel',
    defaultMessage: 'Right arrow'
  },
  learnMore: {
    id: 'ServiceDesk.learnMore',
    defaultMessage: 'Learn more'
  },
  beta: {
    id: 'ServiceDesk.beta',
    defaultMessage: 'Beta'
  }
});

export const Descriptions = defineMessages({
  byo: {
    id: 'ServiceDesk.Descriptions.byo',
    defaultMessage:
      'Integrate your own preferred service desk by using the starter kit available on GitHub.',
    description: ''
  },
  byoPhone: {
    id: 'ServiceDesk.Descriptions.byo',
    defaultMessage:
      "To use your own SIP trunk, configure a SIP trunk from our <link>supported external providers</link>. Copy this SIP address and add it to your SIP trunking provider's original configuration.",
    description: ''
  },
  freePhone: {
    id: 'ServiceDesk.Descriptions.freePhone',
    defaultMessage:
      'Your assistant will send and receive calls at the number below. After the number is ready, give it a call!',
    description: ''
  },
  genesys: {
    id: 'ServiceDesk.Descriptions.genesys',
    defaultMessage:
      'Integrate with Genesys Web Messenger by following the documentation available for web chat.',
    description: ''
  },
  genesysAudioConnector: {
    id: 'ServiceDesk.Descriptions.genesysAudioConnector',
    defaultMessage:
      'Use our integration to enable Genesys Cloud to call Watson Assistant for voice interactions via the Genesys Audio Connector. If your customers ask to speak to someone, your assistant can hand off the call to Genesys to connect your customer with a live agent. <link> Learn more </link>',
    description: ''
  },
  egain: {
    id: 'ServiceDesk.Descriptions.egain',
    defaultMessage:
      'Integrate with eGain by following the step-by-step example available on GitHub.',
    description: ''
  },
  enghouse: {
    id: 'ServiceDesk.Descriptions.enghouse',
    defaultMessage:
      'Integrate with Enghouse by following the step-by-step example available on GitHub.',
    description: ''
  },
  niceIncontact: {
    id: 'ServiceDesk.Descriptions.niceIncontact',
    defaultMessage:
      'Integrate with NICE inContact by following the documentation available for web chat.',
    description: ''
  },
  niceIncontactPhone: {
    id: 'ServiceDesk.Descriptions.niceIncontactPhone',
    defaultMessage:
      'Transfer customers from a chat with your assistant to live agents who can help them by phone. If customers ask to speak to someone, your assistant can forward them directly to customer support with the conversation history. <link>Learn more</link>',
    description: ''
  },
  amazonConnect: {
    id: 'ServiceDesk.Descriptions.amazonConnect',
    defaultMessage:
      "To integrate your account with Amazon Connect, you'll need to log into your AWS Account and follow <link>these steps</link>.",
    description: ''
  },
  kustomer: {
    id: 'ServiceDesk.Descritions.kustomer',
    defaultMessage:
      'Integrate with Kustomer by following the step-by-step example available on GitHub.',
    descrition: ''
  },
  oracle: {
    id: 'ServiceDesk.Descriptions.oracle',
    defaultMessage:
      'Integrate with Oracle by following the step-by-step example available on GitHub.',
    description: ''
  },
  salesforce: {
    id: 'ServiceDesk.Descriptions.salesforce',
    defaultMessage:
      'Enable your assistant to transfer your customers to agents via Salesforce.',
    description: ''
  },
  twilio: {
    id: 'ServiceDesk.Descriptions.twilio',
    defaultMessage:
      'Integrate with Twilio Flex by following the step-by-step example available on GitHub.',
    description: ''
  },
  zendesk: {
    id: 'ServiceDesk.Descriptions.zendesk',
    defaultMessage:
      'Enable your assistant to transfer your customers to agents via Zendesk.',
    description: ''
  }
});

export const Salesforce = defineMessages({
  agentConsoleDescription: {
    id: 'ServiceDesk.Salesforce.agentConsoleDescription',
    defaultMessage:
      'In Salesforce, open the chat app that you created for your agents to talk to customers. Find the chat transcript object, and then open an individual transcript page. Add the Visualforce page that you created earlier to the page layout as a new component, and then save it. Next, activate the new template for the appropriate app and user profiles. <link>Learn more</link>',
    description: ''
  },
  agentConsoleTitle: {
    id: 'ServiceDesk.Salesforce.agentConsoleTitle',
    defaultMessage: 'Add your page to the agent console',
    description: ''
  },
  beginningStep1: {
    id: 'ServiceDesk.Salesforce.beginningStep1',
    defaultMessage: 'Console navigation',
    description: ''
  },
  beginningStep2: {
    id: 'ServiceDesk.Salesforce.beginningStep2',
    defaultMessage: 'Navigation items: Cases, Chat sessions, Chat transcripts',
    description: ''
  },
  beginningStep3: {
    id: 'ServiceDesk.Salesforce.beginningStep3',
    defaultMessage:
      'User profiles: Apply the appropriate profiles to ensure that agents can access the app and view chat history information. You can limit access to this page later. See profiles.',
    description: ''
  },
  beginningStep4: {
    id: 'ServiceDesk.Salesforce.beginningStep4',
    defaultMessage: 'A chat deployment',
    description: ''
  },
  beginningStep5: {
    id: 'ServiceDesk.Salesforce.beginningStep5',
    defaultMessage: 'A chat button deployment',
    description: ''
  },
  beginningStep6: {
    id: 'ServiceDesk.Salesforce.beginningStep6',
    defaultMessage:
      'Routing must be configured for the chat button. See Chat routing options.',
    description: ''
  },
  beginningStep7: {
    id: 'ServiceDesk.Salesforce.beginningStep7',
    defaultMessage:
      'If you choose omni-channel routing, be sure to include omni-channel as a utility in the chat app. See Omni-Channel.',
    description: ''
  },
  beginningStepsTitle: {
    id: 'ServiceDesk.Salesforce.beginningStepsTitle',
    defaultMessage: 'Before you begin, you need:',
    description: ''
  },
  beginningStepsTooltip: {
    id: 'ServiceDesk.Salesforce.beginningStepsTooltip',
    defaultMessage:
      'To connect to a Salesforce service desk, your organization must have a Salesforce Service Cloud plan that supports Live Agent Chat. Chat support is available in Salesforce Service Cloud Unlimited and Enterprise plans. It is also available with Performance or Developer plans that were created after 14 June 2012.',
    description: ''
  },
  chatButtonLabel: {
    id: 'ServiceDesk.Salesforce.chatButtonLabel',
    defaultMessage: 'Chat button code',
    description: ''
  },
  chatButtonPlaceholder: {
    id: 'ServiceDesk.Salesforce.chatButtonPlaceholder',
    defaultMessage: 'Place your button ID here',
    description: ''
  },
  connectDescription1: {
    id: 'ServiceDesk.Salesforce.connectDescription1',
    defaultMessage:
      "To connect, your assistant needs information about your organization's Salesforce chat deployment and chat button.",
    description: ''
  },
  connectDescription2: {
    id: 'ServiceDesk.Salesforce.connectDescription2',
    defaultMessage:
      'To provide your assistant with the information it needs, copy and paste the code snippets from your Salesforce {deploymentsSettingRoute} page and your {chatButtonsSettingRoute} page. ',
    description: ''
  },
  connectDescription2Link: {
    id: 'ServiceDesk.Salesforce.connectDescription2Link',
    defaultMessage: '<link>Learn more</link>',
    description: ''
  },
  connectTitle: {
    id: 'ServiceDesk.Salesforce.connectTitle',
    defaultMessage: 'Connect web chat to your Salesforce service desk',
    description: ''
  },
  deploymentsSettingRoute: {
    id: 'ServiceDesk.Salesforce.deploymentsSettingRoute',
    defaultMessage: 'Feature Settings>Service>Chat>Deployments',
    description: ''
  },
  chatButtonsSettingRoute: {
    id: 'ServiceDesk.Salesforce.chatButtonsSettingRoute',
    defaultMessage: 'Feature Settings>Service>Chat>Chat Buttons & Invitations',
    description: ''
  },
  fieldsAndRelationshipsSettingRoute: {
    id: 'ServiceDesk.Salesforce.fieldsAndRelationshipsSettingRoute',
    defaultMessage:
      'Data>Objects and Fields>Object Manager>Chat Transcript>Fields & Relationships',
    description: ''
  },
  createCustomFieldDescription: {
    id: 'ServiceDesk.Salesforce.createCustomFieldDescription1',
    defaultMessage:
      'From the Salesforce {fieldsAndRelationshipsSettingRoute} page, create the following custom fields:',
    description: ''
  },
  createCustomFieldItem1: {
    id: 'ServiceDesk.Salesforce.createCustomFieldItem1',
    defaultMessage:
      '{createTokenCustomField}: Stores a Watson Assistant authentication token that secures the communication between Salesforce and your assistant.',
    description: ''
  },
  createCustomFieldItem2: {
    id: 'ServiceDesk.Salesforce.createCustomFieldItem2',
    defaultMessage: '{createDataTypeCustomField}: Text Area (Long)',
    description: ''
  },
  createCustomFieldItem3: {
    id: 'ServiceDesk.Salesforce.createCustomFieldItem3',
    defaultMessage: '{createFieldLabelCustomField}: `x-watson-assistant-key`',
    description: ''
  },
  createCustomFieldItem4: {
    id: 'ServiceDesk.Salesforce.createCustomFieldItem4',
    defaultMessage:
      '{createFieldLengthCustomField}: Specify the maximum length allowed to ensure it can hold a token that might contain over 100,000 characters.',
    description: ''
  },
  createTokenCustomField: {
    id: 'ServiceDesk.Salesforce.createTokenCustomField',
    defaultMessage: 'Token',
    description: ''
  },
  createDataTypeCustomField: {
    id: 'ServiceDesk.Salesforce.createDataTypeCustomField',
    defaultMessage: 'Data Type',
    description: ''
  },
  createFieldLabelCustomField: {
    id: 'ServiceDesk.Salesforce.createFieldLabelCustomField',
    defaultMessage: 'Field Label',
    description: ''
  },
  createFieldLengthCustomField: {
    id: 'ServiceDesk.Salesforce.createFieldLengthCustomField',
    defaultMessage: 'Field Length',
    description: ''
  },
  createCustomFieldTitle: {
    id: 'ServiceDesk.Salesforce.createCustomFieldTitle',
    defaultMessage: 'Create custom fields',
    description: ''
  },
  createPageDescription: {
    id: 'ServiceDesk.Salesforce.createPageDescription',
    defaultMessage:
      "When a customer asks to speak to a person, your assistant will transfer the in-progress conversation to the Salesforce agent. To enable this, you'll need a chat app that allows the Salesforce agent to see a history of the chat. If you don't already have one, you'll need to create a Visualforce page and then add a chat app to the page.",
    description: ''
  },
  createPageTitle: {
    id: 'ServiceDesk.Salesforce.createPageTitle',
    defaultMessage:
      'Create a Visualforce page and a chat app to track chat history',
    description: ''
  },
  createVisualForcePageDescription: {
    id: 'ServiceDesk.Salesforce.createVisualForcePageDescription',
    defaultMessage:
      'From your Salesforce web page, search for {visualforcePagesLabel}. Create a page. Add a label and name to the page. Select the {availableForLightningLabel} checkbox. Paste the code snippet below into the page markup field:',
    description: ''
  },
  visualforcePagesLabel: {
    id: 'ServiceDesk.Salesforce.visualforcePagesLabel',
    defaultMessage: 'Visualforce Pages',
    description: ''
  },
  availableForLightningLabel: {
    id: 'ServiceDesk.Salesforce.availableForLightningLabel',
    defaultMessage:
      'Available for Lightning Experience, Lightning Communities, and the mobile app',
    description: ''
  },
  createVisualForcePageTitle: {
    id: 'ServiceDesk.Salesforce.createVisualForcePageTitle',
    defaultMessage: 'Create a Visualforce page',
    description: ''
  },
  deploymentCodeLabel: {
    id: 'ServiceDesk.Salesforce.deploymentCodeLabel',
    defaultMessage: 'Deployment code',
    description: ''
  },
  deploymentCodePlaceholder: {
    id: 'ServiceDesk.Salesforce.deploymentCodePlaceholder',
    defaultMessage: 'Place your deployment code snippet here',
    description: ''
  },
  invalidCode: {
    id: 'ServiceDesk.Salesforce.invalidCode',
    defaultMessage: 'The code contains invalid variables',
    description: ''
  },
  stepOneLabel: {
    id: 'ServiceDesk.Salesforce.stepOneLabel',
    defaultMessage: 'Connect',
    description: ''
  },
  stepOneSecondaryLabel: {
    id: 'ServiceDesk.Salesforce.stepOneSecondaryLabel',
    defaultMessage: 'Salesforce',
    description: ''
  },
  stepTwoLabel: {
    id: 'ServiceDesk.Salesforce.stepTwoLabel',
    defaultMessage: 'Create & add',
    description: ''
  },
  stepTwoSecondaryLabel: {
    id: 'ServiceDesk.Salesforce.stepTwoSecondaryLabel',
    defaultMessage: 'Visualforce page',
    description: ''
  }
});

export const Zendesk = defineMessages({
  accountKeyLabel: {
    id: 'ServiceDesk.Zendesk.accountKeyLabel',
    defaultMessage: 'Account key',
    description: ''
  },
  accountKeyPlaceholder: {
    id: 'ServiceDesk.Zendesk.accountKeyPlaceholder',
    defaultMessage: 'ex. Z2CfxwdZWPpxtNBV1feMTQci652kEshg',
    description: ''
  },
  authenticateUsersDescription1: {
    id: 'ServiceDesk.Zendesk.authenticateUsersDescription1',
    defaultMessage:
      "Add support for verifying a customer's identity when a conversation is transferred to a Zendesk agent. <link>Learn more</link>",
    description: ''
  },
  authenticateUsersDescription2: {
    id: 'ServiceDesk.Zendesk.authenticateUsersDescription2',
    defaultMessage: 'To turn on this feature, you must:',
    description: ''
  },
  authenticateUsersStep1: {
    id: 'ServiceDesk.Zendesk.authenticateUsersStep1',
    defaultMessage: 'enable web chat security',
    description: ''
  },
  authenticateUsersStep2: {
    id: 'ServiceDesk.Zendesk.authenticateUsersStep1',
    defaultMessage: 'setup visitor authentication in Zendesk',
    description: ''
  },
  authenticateUsersTitle: {
    id: 'ServiceDesk.Zendesk.authenticateUsersTitle',
    defaultMessage: 'Authenticate users',
    description: ''
  },
  authenticateUsersToggleDescription: {
    id: 'ServiceDesk.Zendesk.authenticateUsersToggleDescription',
    defaultMessage:
      'Copy the shared secret from Zendesk and add it to the Zendesk shared secret field.',
    description: ''
  },
  chatHistoryDescription: {
    id: 'ServiceDesk.Zendesk.chatHistoryDescription',
    defaultMessage:
      'When a customer asks to speak to a person, your assistant will transfer the in-progress conversation and share a chat summary with the Zendesk agent. To enable the service desk agent to get a quick view of the chat history between the visitor and the assistant, add the Watson Assistant Zendesk app to your Zendesk Chat subdomain. <link>Learn more</link>',
    description: ''
  },
  chatHistoryStep1: {
    id: 'ServiceDesk.Zendesk.chatHistoryStep1',
    defaultMessage: 'Download the Watson Assistant Zendesk app',
    description: ''
  },
  chatHistoryStep2: {
    id: 'ServiceDesk.Zendesk.chatHistoryStep2',
    defaultMessage:
      'Add the Watson Assistant Zendesk app to your Zendesk Chat subdomain.',
    description: ''
  },
  chatHistoryTitle: {
    id: 'ServiceDesk.Zendesk.chatHistoryTitle',
    defaultMessage: 'Share the chat history with service desk agents',
    description: ''
  },
  connectAccountDescription: {
    id: 'ServiceDesk.Zendesk.connectAccountDescription',
    defaultMessage:
      'Add the account key of the Zendesk account that you want to connect to your assistant. <link>Learn more</link>',
    description: ''
  },
  connectAccountInvalid: {
    id: 'ServiceDesk.Zendesk.connectAccountInvalid',
    defaultMessage: 'Account key is invalid',
    description: ''
  },
  connectAccountTitle: {
    id: 'ServiceDesk.Zendesk.connectAccountTitle',
    defaultMessage: 'Connect to your Zendesk account',
    description: ''
  },
  downloadApp: {
    id: 'ServiceDesk.Zendesk.downloadApp',
    defaultMessage: 'downloadApp',
    description: ''
  },
  recommendedAccessDescription: {
    id: 'ServiceDesk.Zendesk.recommendedAccessDescription',
    defaultMessage:
      "To complete this task, you must have administrative access to your organization's Zendesk account. <link>Learn more</link>",
    description: ''
  },
  recommendedAccessTitle: {
    id: 'ServiceDesk.Zendesk.recommendedAccessTitle',
    defaultMessage: 'Recommended access',
    description: ''
  },
  sharedSecretLabel: {
    id: 'ServiceDesk.Zendesk.sharedSecretLabel',
    defaultMessage:
      'Your assistant will use this token to authenticate the transferred conversation.',
    description: ''
  },
  sharedSecretPlaceholder: {
    id: 'ServiceDesk.Zendesk.sharedSecretPlaceholder',
    defaultMessage:
      'ex. 12301A8ABC1937C78F03BD90F7AC079662F041B57C1AAFEC7D7A311BCA7B8G2G',
    description: ''
  },
  sharedSecretTitle: {
    id: 'ServiceDesk.Zendesk.sharedSecretTitle',
    defaultMessage: 'Zendesk shared secret',
    description: ''
  },
  stepOneLabel: {
    id: 'ServiceDesk.Zendesk.stepOneLabel',
    defaultMessage: 'Connect',
    description: ''
  },
  stepOneSecondaryLabel: {
    id: 'ServiceDesk.Zendesk.stepOneSecondaryLabel',
    defaultMessage: 'Zendesk',
    description: ''
  },
  stepTwoLabel: {
    id: 'ServiceDesk.Zendesk.stepTwoLabel',
    defaultMessage: 'Private app',
    description: ''
  },
  stepTwoSecondaryLabel: {
    id: 'ServiceDesk.Zendesk.stepTwoSecondaryLabel',
    defaultMessage: 'Install',
    description: ''
  },
  unidentifiedUsersDescription: {
    id: 'ServiceDesk.Zendesk.unidentifiedUsersDescription',
    defaultMessage:
      'Chats that are initiated by anonymous users are sent to Zendesk unauthenticated. Decide whether you want to take the required additional steps to manage anonymous users. <link>Learn more</link>',
    description: ''
  },
  unidentifiedUsersLabel: {
    id: 'ServiceDesk.Zendesk.unidentifiedUsersLabel',
    defaultMessage: 'Authenticate anonymous user chat transfers',
    description: ''
  },
  unidentifiedUsersTitle: {
    id: 'ServiceDesk.Zendesk.unidentifiedUsersTitle',
    defaultMessage: 'Allow unidentified users to access Zendesk',
    description: ''
  }
});
